import { useState, useEffect, useCallback } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface PasswordFiledProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, errorMsg?: string) => void;
  validationMessage?: string;
  showStarLabel?: boolean;
  disable?: boolean;
  triggerValidation?: boolean;
  minLength?: number;
  maxLength?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  iconName?: string; // ✨ new prop to override default icon
}

const PasswordFiled: React.FC<PasswordFiledProps> = ({
  label,
  value,
  onChange,
  onValidate,
  validationMessage,
  showStarLabel = false,
  disable = false,
  triggerValidation = false,
  minLength = 8,
  maxLength = 32,
  onKeyDown,
  iconName = "unlock.svg", // default stays unlock.svg
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");

  const validate = useCallback(
    (password: string) => {
      let msg = "";
      const trimmed = password.trim();

      if (!trimmed) {
        msg = `${t("please_enter_the")} ${label}`;
      } else if (trimmed.length < minLength) {
        msg = `${label} ${t("must_be_at_least")} ${minLength} ${t("characters")}.`;
      }

      setLocalValidationMessage(msg);
      onValidate?.(label, msg || undefined);
      return !msg;
    },
    [label, minLength, onValidate],
  );

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    onChange(raw);
    if (localValidationMessage) {
      setLocalValidationMessage("");
      onValidate?.(label, undefined);
    }
  };

  const handleBlur = () => {
    validate(value);
  };

  const toggleVisibility = () => setIsVisible((prev) => !prev);

  const message = localValidationMessage || validationMessage;

  useEffect(() => {
    if (triggerValidation) validate(value);
  }, [triggerValidation, value, validate]);

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {label}
        {showStarLabel && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div
        className={`gx_input_cont flex items-center ${
          message ? "invalid" : "valid"
        }`}
      >
        {/* 🔑 Icon can now be customized via props */}
        <img src={`${ICON_BASE_PATH}${iconName}`} alt="Password icon" />

        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          type={isVisible ? "text" : "password"}
          autoComplete="off"
          disabled={disable}
          className="gx-textbox-css"
          maxLength={maxLength}
          aria-invalid={!!message}
        />

        <img
          src={`${ICON_BASE_PATH}${isVisible ? "eyeopen.svg" : "eyeclose.svg"}`}
          alt={isVisible ? "Hide" : "Show"}
          onClick={toggleVisibility}
          className="cursor-pointer invite-code-icon"
          aria-label={isVisible ? "Hide password" : "Show password"}
        />
      </div>

      {message && <div className="gx_input_validationmessage">{message}</div>}
    </div>
  );
};

export default PasswordFiled;
